/**
 * Single source of truth for all URLs, email addresses and domain-tied
 * identifiers used across the app.
 *
 * - Per-environment values are read from `process.env.REACT_APP_*` here (and
 *   ONLY here) so a rebrand/domain change touches the env files, not the tree.
 * - Values that never vary per environment are kept as constants below.
 *
 * When adding a new URL/email/id anywhere in the app, add it here and import it
 * rather than hardcoding a literal or reading `process.env` directly.
 *
 * IMPORTANT: env vars MUST be referenced as static `process.env.REACT_APP_*`
 * member expressions. The Vite build (see `envPlugin` in vite.config.ts) and
 * CRA/webpack before it inline these by textual replacement at build time — a
 * dynamic/computed access like `process.env[key]` is NOT replaced and resolves
 * to `undefined` in the browser bundle.
 */

/**
 * Base domains. These are the primary knobs for the SafetyCulture rebrand —
 * change a host here and every URL composed from it updates.
 */
export const domains = {
  care: "https://care.safetyculture.com",
  safetyculture: "https://safetyculture.com",
} as const;

export const urls = {
  // Env-backed (per-environment — existing env var keys, do not rename)
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL ?? "",
  oauthGrantUrl: process.env.REACT_APP_SAFETYCULTURE_OAUTH_GRANT_URL ?? "",
  iauditorLink: process.env.REACT_APP_IAUDITOR_LINK ?? "",
  brokerLoginLink: process.env.REACT_APP_MITTI_BROKER_LOGIN_LINK ?? "",
  salesforceQuotesUrl: process.env.REACT_APP_SALESFORCE_QUOTES_URL ?? "",
  salesforceNewQuoteUrl: process.env.REACT_APP_SALESFORCE_NEWQUOTE_URL ?? "",
  calendlyVraLink: process.env.REACT_APP_CALENDLY_VRA_LINK ?? "",
  safetycultureAssetsUrl: process.env.REACT_APP_SAFETYCULTURE_ASSETS_URL ?? "",
  mittiWebsite: process.env.REACT_APP_MITTI_WEBSITE ?? "",
  periscopeUrl: process.env.REACT_APP_PERISCOPE_URL ?? "",
  portalUrl: process.env.REACT_APP_PORTAL_URL ?? "",

  // Static (moved from hardcoded component literals), composed from `domains`
  careHome: `${domains.care}/`,
  careContact: `${domains.care}/contact`,
  careContactTrailingSlash: `${domains.care}/contact/`,
  careTerms: `${domains.care}/terms-and-conditions`,
  careTermsTrailingSlash: `${domains.care}/terms-and-conditions/`,
  carePrivacy: `${domains.care}/privacy-policy/`,
  scLegalTerms: `${domains.safetyculture}/legal/terms-and-conditions/`,
  scLegalPrivacy: `${domains.safetyculture}/legal/privacy-policy/`,
  publicLibrarySearch:
    "https://public-library.safetyculture.io/search/?q=mitti&page=1t",
  mittiClaims: "https://www.mitti.com.au/claims",
  googleMapsQuery: "https://maps.google.com/?q=", // callers append encoded address
  youtubeEmbed: "https://www.youtube.com/embed/", // callers append video id
} as const;

/** OAuth logout base, derived from the grant URL (e.g. `.../v2/logout`). */
export const oauthLogoutBase = urls.oauthGrantUrl.replace("/authorize", "");

export const emails = {
  careSupport: process.env.REACT_APP_CARE_SUPPORT_EMAIL ?? "",
  careClaims: process.env.REACT_APP_CARE_CLAIMS_EMAIL ?? "",
  mittiContact: process.env.REACT_APP_MITTI_CONTACT_EMAIL ?? "",
} as const;

export const ids = {
  oauthClientId: process.env.REACT_APP_SAFETYCULTURE_OAUTH_CLIENT_ID ?? "",
  cognitoRegion: process.env.REACT_APP_COGNITO_REGION ?? "",
  cognitoUserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID ?? "",
  cognitoUserPoolWebClientId:
    process.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID ?? "",
  // NOTE: index.html also hardcodes this Google Tag Manager container ID (it
  // can't import this module). Keep the two in sync. It is not domain-tied and
  // is unaffected by the SafetyCulture rebrand.
  gtmId: "GTM-TDGZ277",
  typeformSelfAssessmentId:
    process.env.REACT_APP_TYPEFORM_ID_SELF_ASSESSMENT ?? "",
  evariBpkId: process.env.REACT_APP_EVARI_BPK_ID ?? "",
  evariCpxId: process.env.REACT_APP_EVARI_CPX_ID ?? "",
} as const;
